import { useEffect, useState } from "react";
import { toast } from "sonner";
import { eur, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Banknote, Gift } from "lucide-react";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const CASH_METHODS = [["ESPECES", "Espèces"], ["CB", "CB (TPE)"], ["TICKET", "Ticket resto"], ["AUTRE", "Autre"]];

// Reusable cash / counter collection dialog with change calculation ("monnaie à rendre", never a refund).
export default function CashDialog({ open, onClose, title, dueAmount, allowPartial = false, onConfirm }) {
  const due = round2(dueAmount);
  const [amount, setAmount] = useState(due);
  const [received, setReceived] = useState("");
  const [method, setMethod] = useState("ESPECES");
  const [tipToChange, setTipToChange] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setAmount(round2(dueAmount)); setReceived(""); setMethod("ESPECES"); setTipToChange(false); } }, [open, dueAmount]);

  const amt = round2(amount);
  const rec = received === "" ? null : round2(received);
  const isCash = method === "ESPECES";
  const rawChange = rec != null ? round2(rec - amt) : null;             // monnaie à rendre théorique
  const change = tipToChange ? 0 : (rawChange != null ? Math.max(0, rawChange) : null);
  const tip = tipToChange && rawChange != null ? Math.max(0, rawChange) : 0;
  const partial = amt < due - 0.01;

  const confirm = async () => {
    if (amt <= 0) { toast.error("Montant invalide"); return; }
    if (amt > due + 0.01) { toast.error(`Maximum : ${eur(due)}`); return; }
    if (isCash && rec != null && rec < amt - 0.01) { toast.error("Le montant reçu est inférieur au montant à régler"); return; }
    setSaving(true);
    try {
      await onConfirm({ amount: amt, amount_received: isCash ? (rec ?? undefined) : undefined, tip, method, change: change ?? 0 });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cash-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2"><Banknote className="w-5 h-5 text-[#C69C4A]" /> {title || "Encaissement"}</DialogTitle>
          <DialogDescription className="text-stone-400">Reste à payer : <span className="font-bold text-white">{eur(due)}</span></DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-1.5" data-testid="cash-method-picker">
            {CASH_METHODS.map(([m, l]) => (
              <button key={m} data-testid={`cash-method-${m}`} onClick={() => setMethod(m)}
                className={`rounded-xl py-2 text-xs font-semibold border transition-colors ${method === m ? "bg-[#1A3D32] text-white border-[#2C5446]" : "bg-[#111F1A] text-stone-400 border-[#1B3028]"}`}>{l}</button>
            ))}
          </div>

          {allowPartial && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Montant à encaisser (€)</label>
              <Input data-testid="cash-amount" type="number" min="0" step="0.5" max={due} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 bg-[#111F1A] border-[#1B3028] text-stone-100 rounded-xl" />
              {partial && <p className="text-[11px] text-[#E7C97A] mt-1">Paiement partiel · restera {eur(round2(due - amt))} à régler → table PARTIELLEMENT PAYÉE</p>}
            </div>
          )}

          {isCash && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Montant reçu (€)</label>
              <Input data-testid="cash-received" type="number" min="0" step="0.5" value={received} onChange={(e) => setReceived(e.target.value)} placeholder={`ex : ${Math.ceil(amt / 10) * 10}`} className="mt-1.5 bg-[#111F1A] border-[#1B3028] text-stone-100 rounded-xl" />
              {rawChange != null && rawChange > 0 && (
                <div className="mt-3 rounded-xl bg-[#111F1A] border border-[#1B3028] p-3 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-stone-400">Montant réglé</span><span className="text-white font-semibold">{eur(amt)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-stone-400">Reçu</span><span className="text-white font-semibold">{eur(rec)}</span></div>
                  <button data-testid="cash-tip-toggle" onClick={() => setTipToChange((v) => !v)}
                    className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold border ${tipToChange ? "bg-[#C69C4A]/15 border-[#C69C4A]/50 text-[#E7C97A]" : "bg-[#0A1712] border-[#1B3028] text-stone-400"}`}>
                    <Gift className="w-3.5 h-3.5" /> Le client laisse la monnaie en pourboire
                  </button>
                  <div className="flex justify-between border-t border-[#1B3028] pt-2">
                    <span className="text-stone-200 font-bold">{tipToChange ? "Pourboire" : "Monnaie à rendre"}</span>
                    <span data-testid="cash-change" className="text-2xl font-extrabold" style={{ color: tipToChange ? "#C69C4A" : "#34D399" }}>{eur(tipToChange ? tip : change)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full bg-transparent border-[#1B3028] text-stone-300">Annuler</Button>
          <Button data-testid="cash-confirm" onClick={confirm} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            {saving ? "..." : `Confirmer l'encaissement · ${eur(amt)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
